/**
 * ExtractionValidator v1.0
 * Circuit breaker para validación pre-geocodificación
 * 
 * Sprint 1 - 9 Enero 2026
 * 
 * Funciones implementadas:
 * 1. extractMunicipalityFromContent() - Busca municipio en contenido del documento
 * 2. isHydrographyTable() - Detecta tablas de ríos/arroyos (falsos positivos)
 * 3. validateMunicipality() - Valida contra lista negra y formato
 * 4. detectMalformedCoordinates() - Detecta coords con formato incorrecto (8 dígitos)
 * 5. validateExtraction() - Validación completa pre-geocodificación
 * 
 * @module lib/ExtractionValidator
 */

import { buscarMunicipio, normalizarNombreMunicipio as _normalizarNombreMunicipio } from './codigosINEUnificado';

// ============================================================================
// TIPOS
// ============================================================================

export interface ValidationResult {
  isValid: boolean;
  alerts: ValidationAlert[];
  suggestions: Record<string, string>;
  municipalityFromContent: string | null;
  excludedHydroTables: number;
  malformedCoords: MalformedCoordinate[];
}

export interface ValidationAlert {
  level: 'error' | 'warning' | 'info';
  code: string;
  message: string;
  affectedItems?: number;
}

export interface MalformedCoordinate {
  original: string;
  type: 'x' | 'y';
  problem: 'too_many_digits' | 'too_few_digits' | 'invalid_range';
  suggestedFix?: string;
}

// ============================================================================
// CONSTANTES
// ============================================================================

/**
 * Lista negra: términos que NUNCA son municipios válidos.
 * Si detectMunicipality() devuelve alguno de estos, hay un bug.
 */
const MUNICIPALITY_BLACKLIST = new Set([
  'ayuntamiento',
  'ayto',
  'municipio',
  'ptel',
  'ficha',
  'plantilla',
  'coordenadas',
  'infraestructuras',
  'elementos',
  'vulnerables',
  'recursos',
  'materiales',
]);

/**
 * Patrones para detectar tablas de hidrografía (ríos, arroyos, cauces).
 * Estas tablas contienen longitudes en km/m, NO coordenadas UTM.
 */
const HYDRO_HEADER_PATTERNS = [
  /longitud\s*\(km\)/i,
  /longitud\s*\(m\)/i,
  /longitud\s+dentro\s+del\s+municipio/i,
  /longitud\s+total/i,
  /cota\s+m[áa]xima/i,
  /cota\s+m[ií]nima/i,
  /cuenca\s+hidrogr[áa]fica/i,
  /superficie\s+de\s+cuenca/i,
  /caudal/i,
];

/**
 * Patrones para detectar contenido de hidrografía en primera columna.
 */
const HYDRO_CONTENT_PATTERNS = [
  /^arroyo\s/i,
  /^r[ií]o\s/i,
  /^rambla\s/i,
  /^barranco\s/i,
  /^ca[ñn]ada\s/i,
  /^regato\s/i,
  /^torrente\s/i,
  /^acequia\s/i,
];

// ============================================================================
// 1. EXTRACCIÓN DE MUNICIPIO DESDE CONTENIDO
// ============================================================================

/**
 * Sprint 1 FIX: Busca el nombre del municipio DIRECTAMENTE en el XML del documento.
 * 
 * OPTIMIZACIÓN: En lugar de limpiar todo el XML y luego buscar, 
 * buscamos el patrón directamente en el XML crudo. Esto es ~10x más rápido
 * porque evitamos crear un string intermedio de texto limpio.
 * 
 * El texto "Ayuntamiento de La Guardia de Jaén" aparece en el XML como:
 * <text:p>Ayuntamiento de La Guardia de Jaén</text:p>
 * 
 * Estrategia:
 * 1. Buscar patrón "Ayuntamiento de [Municipio]" directamente en XML
 * 2. Contar ocurrencias y devolver el más frecuente
 * 3. Validar contra lista negra
 * 
 * @param xmlContent - Contenido XML COMPLETO del documento (NO texto limpio)
 * @returns Nombre del municipio encontrado, o null si no se detecta
 */
export function extractMunicipalityFromContent(xmlContent: string): string | null {
  if (!xmlContent || typeof xmlContent !== 'string') return null;
  
  // Patrones que buscan directamente en XML (el texto está entre tags)
  // Capturamos hasta encontrar un tag de cierre "<" o fin de línea
  const patterns = [
    // "Ayuntamiento de La Guardia de Jaén" - más común y fiable
    /Ayuntamiento\s+de\s+([A-ZÁÉÍÓÚÑ][A-Za-záéíóúñÁÉÍÓÚÑ\s\-']{2,50}?)(?:\s*[<,\.\(\)\n]|$)/gi,
    // "Municipio de Guadix"
    /Municipio\s+de\s+([A-ZÁÉÍÓÚÑ][A-Za-záéíóúñÁÉÍÓÚÑ\s\-']{2,50}?)(?:\s*[<,\.\(\)\n]|$)/gi,
    // "PTEL de Jubrique"
    /PTEL\s+de\s+([A-ZÁÉÍÓÚÑ][A-Za-záéíóúñÁÉÍÓÚÑ\s\-']{2,50}?)(?:\s*[<,\.\(\)\n]|$)/gi,
  ];
  
  // Contar ocurrencias de cada municipio encontrado
  const counts = new Map<string, number>();
  
  for (const pattern of patterns) {
    // Reset lastIndex para cada patrón (importante con flag 'g')
    pattern.lastIndex = 0;
    
    let match;
    while ((match = pattern.exec(xmlContent)) !== null) {
      let muni = match[1].trim();
      
      // Limpiar: eliminar partículas sueltas al final
      muni = muni
        .replace(/\s+(de|del|la|las|los|el)\s*$/i, '')
        .replace(/\s+provincia\s*$/i, '')
        .trim();
      
      // MJ-03: Eliminar sufijos de entidad poblacional (Pue, Nuc, Ald…)
      muni = cleanMunicipalityName(muni);

      // BUG-03-S1: Truncar ante verbos/preposiciones de encabezado narrativo
      // Preserva "La Guardia de Jaén", "El Cuervo de Sevilla" (prep. como parte del nombre)
      const CORTE_NARRATIVO = /\s+(?:se\s+enmarca|se\s+ubica|pertenece\s+a|está\s+situad|es\s+un|en\s+la\s+provincia\s+de|de\s+la\s+provincia)\b/i;
      muni = muni.split(CORTE_NARRATIVO)[0].trim();

      // BUG-03-S2: Ningún topónimo andaluz supera 5 palabras (máx: "La Guardia de Jaén" = 4)
      if (muni.split(/\s+/).length > 5) continue;

      // Saltar si es muy corto o está en blacklist
      if (muni.length < 3) continue;
      const normalized = muni.toLowerCase();
      if (MUNICIPALITY_BLACKLIST.has(normalized)) continue;
      
      // Normalizar capitalización
      muni = normalizeMunicipalityCase(muni);
      
      counts.set(muni, (counts.get(muni) || 0) + 1);
    }
  }
  
  // Devolver el más frecuente
  if (counts.size === 0) return null;
  
  let best = '';
  let bestCount = 0;
  for (const [muni, count] of counts) {
    if (count > bestCount) {
      best = muni;
      bestCount = count;
    }
  }
  
  // Log para debugging
  if (best) {
    console.log(`[ExtractionValidator] Municipio detectado en contenido: "${best}" (${bestCount} ocurrencias)`);
  }
  
  return best || null;
}

// ============================================================================
// MJ-03 — LIMPIEZA DE SUFIJOS DE ENTIDAD POBLACIONAL
// ============================================================================

/**
 * Sufijos que aparecen en documentos ODT tras el nombre del municipio cuando
 * el documento hace referencia a una entidad singular de población (Pueblo,
 * Núcleo, Aldea, Diseminado, Barrio, Agrupación) en formato abreviado o completo.
 *
 * Ejemplo real: "El Cuervo de Sevilla Pue" → "El Cuervo de Sevilla"
 */
const MUNICIPALITY_SUFFIXES =
  /\s+(Pueblo|Pue\.?|N[uú]cleo|Nuc\.?|Aldea|Ald\.?|Diseminado|Dis\.?|Barrio|Bar\.?|Agrupaci[oó]n|Agr\.?)[\s,.)]*/gi;

/**
 * Elimina sufijos de entidad poblacional que aparecen tras el nombre del municipio.
 * "El Cuervo de Sevilla Pue" → "El Cuervo de Sevilla"
 */
export function cleanMunicipalityName(raw: string): string {
  return raw.replace(MUNICIPALITY_SUFFIXES, '').trim();
}

/**
 * Normaliza la capitalización de un nombre de municipio.
 * "LA GUARDIA DE JAÉN" → "La Guardia de Jaén"
 * "colomera" → "Colomera"
 */
function normalizeMunicipalityCase(name: string): string {
  const particles = new Set(['de', 'del', 'la', 'las', 'los', 'el', 'y', 'e']);
  
  return name
    .toLowerCase()
    .split(/\s+/)
    .map((word, idx) => {
      // Primera palabra siempre capitalizada
      if (idx === 0) {
        return word.charAt(0).toUpperCase() + word.slice(1);
      }
      // Partículas en minúscula
      if (particles.has(word)) {
        return word;
      }
      // Resto capitalizadas
      return word.charAt(0).toUpperCase() + word.slice(1);
    })
    .join(' ');
}

// ============================================================================
// 2. DETECCIÓN DE TABLAS DE HIDROGRAFÍA
// ============================================================================

/**
 * Detecta si una tabla es de hidrografía (ríos, arroyos, cauces).
 * 
 * Estas tablas contienen datos como:
 * - "Longitud (km)": 32.4, 14.9, 11.6 (longitudes de cauces, NO coordenadas)
 * - "Cota Máxima (m)": altitudes
 * - Nombres: "Arroyo de...", "Río...", "Rambla..."
 * 
 * @param headers - Array de strings con los headers de la tabla
 * @param firstColumnValues - Opcional: valores de la primera columna para verificar contenido
 * @returns true si es tabla de hidrografía (debe excluirse)
 */
export function isHydrographyTable(
  headers: string[],
  firstColumnValues?: string[]
): boolean {
  if (!headers || headers.length === 0) return false;
  
  const headerText = headers.join(' ').toLowerCase();
  
  // Verificar headers típicos de hidrografía
  const hasHydroHeader = HYDRO_HEADER_PATTERNS.some(p => p.test(headerText));
  if (hasHydroHeader) {
    // Si tiene "Longitud (km)" o similar, es definitivamente hidrografía
    return true;
  }
  
  // Verificar contenido de primera columna si está disponible
  if (firstColumnValues && firstColumnValues.length > 0) {
    const hydroContentCount = firstColumnValues.filter(val => 
      HYDRO_CONTENT_PATTERNS.some(p => p.test(val))
    ).length;
    
    // Si más del 50% de valores son nombres de cauces, es tabla de hidrografía
    if (hydroContentCount > firstColumnValues.length * 0.5) {
      return true;
    }
  }
  
  // Verificar combinación de headers sospechosos
  const suspiciousHeaders = [
    /^nombre$/i,
    /longitud/i,
    /cuenca/i,
    /cota/i,
  ];
  
  const matchCount = headers.filter(h => 
    suspiciousHeaders.some(p => p.test(h.trim()))
  ).length;
  
  // Si tiene 3+ headers sospechosos Y NO tiene headers de coordenadas UTM
  if (matchCount >= 3) {
    const hasCoordHeaders = headers.some(h => 
      /\b(utm|coordenada|x|y|easting|northing)\b/i.test(h)
    );
    if (!hasCoordHeaders) {
      return true;
    }
  }
  
  return false;
}

/**
 * Verifica si un valor numérico parece ser una longitud de cauce (NO coordenada).
 * 
 * Longitudes típicas de cauces en Andalucía: 0.5 - 500 km
 * Coordenadas UTM X: 100,000 - 800,000
 * Coordenadas UTM Y: 4,000,000 - 4,350,000
 * 
 * @param value - Valor numérico a verificar
 * @returns true si parece longitud de cauce
 */
export function looksLikeRiverLength(value: string): boolean {
  const cleaned = value.replace(/[,\s]/g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  
  if (isNaN(num)) return false;
  
  // Longitudes típicas de cauces: 0.1 - 500 km o 100 - 50000 m
  // Coordenadas X: 100000+ | Coordenadas Y: 4000000+
  // Si el número es < 100000, NO puede ser coordenada UTM válida
  return num > 0 && num < 100000;
}

// ============================================================================
// 3. VALIDACIÓN DE MUNICIPIO
// ============================================================================

/**
 * Valida si un string es un municipio válido (no está en blacklist).
 * 
 * NO verifica contra la lista INE completa (eso lo hace buscarMunicipioINE).
 * Esta función es un filtro rápido para detectar errores obvios.
 * 
 * @param municipality - Nombre del municipio a validar
 * @returns true si pasa validación básica
 */
export function validateMunicipality(municipality: string): {
  isValid: boolean;
  reason?: string;
} {
  if (!municipality || typeof municipality !== 'string') {
    return { isValid: false, reason: 'Municipio vacío o inválido' };
  }
  
  const normalized = municipality.toLowerCase().trim();
  
  // Verificar blacklist
  if (MUNICIPALITY_BLACKLIST.has(normalized)) {
    return { 
      isValid: false, 
      reason: `"${municipality}" no es un municipio (término genérico)` 
    };
  }
  
  // Verificar longitud mínima
  if (normalized.length < 3) {
    return { 
      isValid: false, 
      reason: `"${municipality}" es demasiado corto para ser un municipio` 
    };
  }
  
  // Verificar que no sea solo números
  if (/^\d+$/.test(normalized)) {
    return { 
      isValid: false, 
      reason: `"${municipality}" es solo números` 
    };
  }
  
  // Verificar contra INE (búsqueda exacta o parcial)
  const ineResult = buscarMunicipio(municipality);
  if (!ineResult.encontrado) {
    // No está en INE, pero puede ser válido con variación ortográfica
    return { 
      isValid: true, // Permitimos, pero se podría advertir
      reason: `"${municipality}" no encontrado exactamente en INE (puede ser variación ortográfica)`
    };
  }
  
  return { isValid: true };
}

// ============================================================================
// 4. DETECCIÓN DE COORDENADAS MALFORMADAS
// ============================================================================

/**
 * Detecta coordenadas con formato incorrecto.
 * 
 * Problemas comunes:
 * - Y con 8 dígitos: 41780021 → debería ser 4178002.1
 * - X con 5 dígitos: 52760 → truncada, falta un dígito
 * - Decimal mal ubicado: 4178002195 → debería ser 4178002.195
 * 
 * Rangos válidos Andalucía (EPSG:25830):
 * - X (Easting): 100,000 - 620,000 (6 dígitos enteros)
 * - Y (Northing): 3,980,000 - 4,290,000 (7 dígitos enteros, empieza 39-42)
 * 
 * @param value - Valor de coordenada a verificar
 * @param type - Tipo de coordenada ('x' o 'y')
 * @returns Información sobre el problema detectado, o null si OK
 */
export function detectMalformedCoordinate(
  value: string,
  type: 'x' | 'y'
): MalformedCoordinate | null {
  if (!value || typeof value !== 'string') return null;
  
  // Limpiar valor
  let cleaned = value.trim().replace(/\s/g, '');
  cleaned = cleaned.replace(',', '.'); // Coma por punto
  
  // Extraer parte entera y decimal
  const parts = cleaned.split('.');
  const integerPart = parts[0].replace(/[^\d]/g, '');
  const decimalPart = parts[1] || '';
  
  if (!integerPart) return null;
  
  const intDigits = integerPart.length;
  const num = parseFloat(cleaned);
  
  if (isNaN(num)) return null;
  
  // ========== COORDENADA Y (Northing) ==========
  if (type === 'y') {
    // Y válida: 7 dígitos enteros, rango 3980000-4290000
    
    // Caso 1: 8 dígitos enteros (41780021) → decimal mal ubicado
    if (intDigits === 8 && integerPart.startsWith('4')) {
      const suggestedFix = integerPart.slice(0, 7) + '.' + integerPart.slice(7) + decimalPart;
      return {
        original: value,
        type: 'y',
        problem: 'too_many_digits',
        suggestedFix,
      };
    }
    
    // Caso 2: 6 dígitos (417800) → falta un dígito
    if (intDigits === 6) {
      return {
        original: value,
        type: 'y',
        problem: 'too_few_digits',
        suggestedFix: `Posible: 4${integerPart} o ${integerPart}0`,
      };
    }
    
    // Caso 3: Valor fuera de rango Andalucía
    if (intDigits === 7) {
      const intVal = parseInt(integerPart);
      if (intVal < 3980000 || intVal > 4290000) {
        return {
          original: value,
          type: 'y',
          problem: 'invalid_range',
          suggestedFix: `Rango válido: 3980000-4290000`,
        };
      }
    }
  }
  
  // ========== COORDENADA X (Easting) ==========
  if (type === 'x') {
    // X válida: 6 dígitos enteros, rango 100000-620000
    
    // Caso 1: 5 dígitos (52760) → truncada
    if (intDigits === 5) {
      return {
        original: value,
        type: 'x',
        problem: 'too_few_digits',
        suggestedFix: `Posible: ${integerPart}0 o 5${integerPart}`,
      };
    }
    
    // Caso 2: 7 dígitos (4307916) → puede ser Y mal ubicada
    if (intDigits === 7) {
      const intVal = parseInt(integerPart);
      // Si empieza con 39-43, probablemente es una Y
      if (intVal >= 3900000 && intVal <= 4350000) {
        return {
          original: value,
          type: 'x',
          problem: 'invalid_range',
          suggestedFix: `Parece coordenada Y, no X`,
        };
      }
    }
    
    // Caso 3: Valor fuera de rango Andalucía
    if (intDigits === 6) {
      const intVal = parseInt(integerPart);
      if (intVal < 100000 || intVal > 620000) {
        return {
          original: value,
          type: 'x',
          problem: 'invalid_range',
          suggestedFix: `Rango válido: 100000-620000`,
        };
      }
    }
  }
  
  return null;
}

/**
 * Analiza un array de coordenadas y devuelve las malformadas.
 */
export function detectMalformedCoordinates(
  coordinates: Array<{ x?: string; y?: string }>
): MalformedCoordinate[] {
  const malformed: MalformedCoordinate[] = [];
  
  for (const coord of coordinates) {
    if (coord.x) {
      const xProblem = detectMalformedCoordinate(coord.x, 'x');
      if (xProblem) malformed.push(xProblem);
    }
    if (coord.y) {
      const yProblem = detectMalformedCoordinate(coord.y, 'y');
      if (yProblem) malformed.push(yProblem);
    }
  }
  
  return malformed;
}

// ============================================================================
// 5. VALIDACIÓN COMPLETA PRE-GEOCODIFICACIÓN
// ============================================================================

/**
 * Realiza validación completa de extracción antes de geocodificación.
 * 
 * @param detectedMunicipality - Municipio detectado del filename
 * @param documentContent - Texto del documento para buscar municipio
 * @param infrastructures - Infraestructuras extraídas (opcional)
 * @returns Resultado de validación con alertas y sugerencias
 */
export function validateExtraction(
  detectedMunicipality: string | undefined,
  documentContent: string,
  infrastructures?: Array<{ xOriginal?: string; yOriginal?: string; hasCoordinates?: boolean }>
): ValidationResult {
  const alerts: ValidationAlert[] = [];
  const suggestions: Record<string, string> = {};
  const malformedCoords: MalformedCoordinate[] = [];
  
  // 1. Buscar municipio en contenido
  const municipalityFromContent = extractMunicipalityFromContent(documentContent);
  
  // 2. Validar municipio detectado
  if (detectedMunicipality) {
    const validation = validateMunicipality(detectedMunicipality);
    if (!validation.isValid) {
      alerts.push({
        level: 'error',
        code: 'MUNICIPALITY_INVALID',
        message: validation.reason || `"${detectedMunicipality}" no es válido`,
      });
      
      // Sugerir alternativa desde contenido
      if (municipalityFromContent) {
        suggestions['municipality'] = municipalityFromContent;
        alerts.push({
          level: 'info',
          code: 'MUNICIPALITY_SUGGESTION',
          message: `Encontrado en documento: "${municipalityFromContent}"`,
        });
      }
    }
  } else {
    // No se detectó municipio del filename
    alerts.push({
      level: 'warning',
      code: 'MUNICIPALITY_MISSING',
      message: 'No se detectó municipio del nombre de archivo',
    });
    
    if (municipalityFromContent) {
      suggestions['municipality'] = municipalityFromContent;
      alerts.push({
        level: 'info',
        code: 'MUNICIPALITY_SUGGESTION',
        message: `Encontrado en documento: "${municipalityFromContent}"`,
      });
    }
  }
  
  // 3. Detectar coordenadas malformadas
  if (infrastructures && infrastructures.length > 0) {
    const coords = infrastructures
      .filter(i => i.xOriginal || i.yOriginal)
      .map(i => ({ x: i.xOriginal, y: i.yOriginal }));
    
    const detected = detectMalformedCoordinates(coords);
    malformedCoords.push(...detected);
    
    if (detected.length > 0) {
      const byType = {
        too_many_digits: detected.filter(d => d.problem === 'too_many_digits').length,
        too_few_digits: detected.filter(d => d.problem === 'too_few_digits').length,
        invalid_range: detected.filter(d => d.problem === 'invalid_range').length,
      };
      
      if (byType.too_many_digits > 0) {
        alerts.push({
          level: 'warning',
          code: 'COORDS_8_DIGITS',
          message: `${byType.too_many_digits} coordenadas con 8 dígitos (posible decimal mal ubicado)`,
          affectedItems: byType.too_many_digits,
        });
      }
      
      if (byType.too_few_digits > 0) {
        alerts.push({
          level: 'warning',
          code: 'COORDS_TRUNCATED',
          message: `${byType.too_few_digits} coordenadas truncadas (faltan dígitos)`,
          affectedItems: byType.too_few_digits,
        });
      }
      
      if (byType.invalid_range > 0) {
        alerts.push({
          level: 'warning',
          code: 'COORDS_OUT_OF_RANGE',
          message: `${byType.invalid_range} coordenadas fuera del rango de Andalucía`,
          affectedItems: byType.invalid_range,
        });
      }
    }
    
    // 4. Calcular tasa de éxito
    const withCoords = infrastructures.filter(i => i.hasCoordinates).length;
    const total = infrastructures.length;
    
    if (total > 5 && withCoords / total < 0.2) {
      alerts.push({
        level: 'info',
        code: 'LOW_COORD_RATE',
        message: `Solo ${Math.round(withCoords / total * 100)}% de infraestructuras tienen coordenadas`,
        affectedItems: total - withCoords,
      });
    }
  }
  
  // Determinar si la extracción es válida (sin errores críticos)
  const hasErrors = alerts.some(a => a.level === 'error');
  
  return {
    isValid: !hasErrors,
    alerts,
    suggestions,
    municipalityFromContent,
    excludedHydroTables: 0, // Se actualizará durante extracción
    malformedCoords,
  };
}

// ============================================================================
// EXPORTS ADICIONALES PARA TESTING
// ============================================================================

export { MUNICIPALITY_BLACKLIST, HYDRO_HEADER_PATTERNS, HYDRO_CONTENT_PATTERNS };
